import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { SplitReveal, FadeUp } from "../components/SplitReveal";
import { useLanguage } from "../lib/i18n";
import { GALLERY } from "../lib/images";

function Row({ images, direction, onOpen, offset }) {
  const doubled = [...images, ...images];
  return (
    <div className="group/row relative flex overflow-hidden">
      <div
        className="flex shrink-0 gap-5 pr-5"
        style={{
          animation: `${direction === "left" ? "marquee-left" : "marquee-right"} 40s linear infinite`,
        }}
      >
        {doubled.map((src, i) => (
          <button
            key={i}
            onClick={() => onOpen((offset + (i % images.length)) % GALLERY.length)}
            data-cursor
            data-cursor-label="AÇ"
            data-testid={`gallery-item-${direction}-${i}`}
            className="relative h-56 w-80 shrink-0 overflow-hidden rounded-2xl border border-white/10 opacity-90 transition-all duration-500 hover:!opacity-100 hover:scale-[1.04] group-hover/row:opacity-40"
          >
            <img src={src} alt="HARSPRO galeri" loading="lazy" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

export function Gallery() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(null);
  const half = Math.ceil(GALLERY.length / 2);
  const rowA = GALLERY.slice(0, half);
  const rowB = GALLERY.slice(half);

  const close = useCallback(() => setOpen(null), []);
  const next = useCallback(() => setOpen((i) => (i + 1) % GALLERY.length), []);
  const prev = useCallback(() => setOpen((i) => (i - 1 + GALLERY.length) % GALLERY.length), []);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, next, prev]);

  return (
    <section id="gallery" className="relative overflow-hidden bg-[#0F1115] py-24 md:py-40" data-testid="gallery-section">
      <div className="mx-auto mb-14 max-w-[1600px] px-6 md:px-12">
        <FadeUp>
          <p className="heading mb-4 text-sm not-italic tracking-[0.3em] text-hars-orange">{t.gallery.kicker}</p>
        </FadeUp>
        <SplitReveal text={t.gallery.title} className="heading text-5xl text-white md:text-7xl" />
        <FadeUp delay={0.1}>
          <p className="mt-4 font-inter text-sm text-hars-muted">{t.gallery.text}</p>
        </FadeUp>
      </div>

      <div className="space-y-5">
        <Row images={rowA} direction="left" onOpen={setOpen} offset={0} />
        <Row images={rowB} direction="right" onOpen={setOpen} offset={half} />
      </div>

      {open !== null && (
        <div
          className="fixed inset-0 z-[9996] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={close}
          data-testid="gallery-lightbox"
        >
          <button onClick={close} className="absolute right-6 top-6 text-white/70 hover:text-white" aria-label="Kapat" data-testid="lightbox-close">
            <X size={32} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 md:left-10 text-white/70 hover:text-hars-orange"
            aria-label="Önceki"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={44} />
          </button>
          <img
            src={GALLERY[open]}
            alt="HARSPRO galeri büyük görsel"
            className="max-h-[85vh] max-w-[90vw] rounded-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 md:right-10 text-white/70 hover:text-hars-orange"
            aria-label="Sonraki"
            data-testid="lightbox-next"
          >
            <ChevronRight size={44} />
          </button>
        </div>
      )}
    </section>
  );
}
